import { z } from 'zod';
import { duelStatusSchema } from './enums.js';
/** API response schemas for duels list/detail (shared web + contract tests). */
export const duelStatusResponseSchema = duelStatusSchema;
export const aiPredictionSchema = z.object({
    username: z.string(),
    selected_player_id: z.string().uuid(),
    predictor_rating: z.number(),
});
const duelPlayerInDuelSchema = z.object({
    id: z.string().uuid(),
    fpl_id: z.number().int(),
    web_name: z.string(),
    first_name: z.string().optional(),
    second_name: z.string().optional(),
    element_type: z.number().int(),
    team: z.object({
        id: z.string().uuid(),
        name: z.string(),
        short_name: z.string(),
        code: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
    }),
    photo: z.string().nullable().optional(),
    now_cost: z.number().int().optional(),
    total_points: z.number().int().optional(),
    form: z.string().optional(),
    selected_by_percent: z.string().optional(),
    availability_pct: z.number().int().min(0).max(100).nullable().optional(),
    availability_status: z.string().nullable().optional(),
    availability_news: z.string().nullable().optional(),
    solio_projected_points: z.number().nullable().optional(),
    gameweek_stats: z
        .object({
        total_points: z.number().int().nullable().optional(),
        is_final: z.boolean().nullable().optional(),
        minutes: z.number().int().nullable().optional(),
        fixtures: z
            .array(z.object({
            fixture_fpl_id: z.number().int(),
            finished_provisional: z.boolean().nullable().optional(),
            finished: z.boolean().nullable().optional(),
            started: z.boolean().nullable().optional(),
            team_h_score: z.number().int().nullable().optional(),
            team_a_score: z.number().int().nullable().optional(),
            kickoff_time: z.string().nullable().optional(),
            player_points: z.number().int().nullable().optional(),
            player_minutes: z.number().int().nullable().optional(),
            player_is_final: z.boolean().nullable().optional(),
            team_h: z
                .object({
                id: z.string().uuid(),
                fpl_id: z.number().int(),
                name: z.string(),
                short_name: z.string(),
                code: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
            })
                .nullable()
                .optional(),
            team_a: z
                .object({
                id: z.string().uuid(),
                fpl_id: z.number().int(),
                name: z.string(),
                short_name: z.string(),
                code: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
            })
                .nullable()
                .optional(),
        }))
            .nullable()
            .optional(),
    })
        .nullable()
        .optional(),
});
export const duelSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    deadline_time: z.string(),
    player_a_id: z.string().uuid().nullable(),
    player_b_id: z.string().uuid().nullable(),
    gameweek_id: z.string().uuid().nullable(),
    status: duelStatusResponseSchema,
    outcome: z.string().nullable(),
    outcome_reason: z.string().nullable(),
    is_active: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
    gameweek_fpl_id: z.number().nullable(),
    og_card_url: z.string().nullable().optional(),
    is_duel_of_the_week: z.boolean().optional(),
    rating_multiplier: z.number().optional(),
    player_a: duelPlayerInDuelSchema.nullable().optional(),
    player_b: duelPlayerInDuelSchema.nullable().optional(),
    votes: z
        .object({
        player_a_votes: z.number().int(),
        player_b_votes: z.number().int(),
        total_votes: z.number().int(),
        user_vote: z.string().uuid().nullable(),
        pick_change_count: z.number().int().nonnegative().optional(),
        last_change_at: z.string().nullable().optional(),
        had_early_bonus_on_current_pick: z.boolean().optional(),
        prediction_outcome: z.enum(['correct', 'incorrect', 'tie', 'voided', 'no_vote']).nullable().optional(),
        rating_change: z.number().nullable().optional(),
    })
        .optional(),
    potential_rating: z
        .object({
        if_player_a_wins: z.number(),
        if_player_a_loses: z.number(),
        if_player_a_tie: z.number(),
        if_player_b_wins: z.number(),
        if_player_b_loses: z.number(),
        if_player_b_tie: z.number(),
        selected_player_id: z.string().uuid().nullable(),
        kPop: z.number(),
        kBase: z.number(),
        kEarly: z.number(),
        rating_multiplier: z.number().optional(),
    })
        .nullable()
        .optional(),
    rating_factors: z
        .object({
        duel_weight: z.string(),
        duel_weight_detail: z.string(),
        early_bonus_value: z.string(),
        early_bonus_detail: z.string(),
        is_duel_of_the_week: z.boolean().optional(),
        rating_factors_debug: z
            .object({
            N: z.number(),
            Nmin: z.number(),
            Nmax: z.number(),
            kPop_formula: z.string(),
            hours_before_deadline: z.number().optional(),
            kEarly_bracket: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
    ai_predictions: z.array(aiPredictionSchema).default([]),
    league_split: z
        .object({
        league_id: z.string().uuid(),
        p1_votes: z.number().int().nonnegative(),
        p2_votes: z.number().int().nonnegative(),
        voted: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    })
        .nullable()
        .optional(),
});
export const duelsResponseSchema = z.object({
    duels: z.array(duelSchema),
    pagination: z.object({
        limit: z.number().int().nonnegative(),
        offset: z.number().int().nonnegative(),
        total_count: z.number().int().nonnegative(),
    }),
});
